import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Grid2,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import { format } from 'date-fns';
import config from "../common/Config";
import AccountService from '../../services/AccountServices';
import '../common/commonStyles.css';

const MyLadger = () => {
  const accessToken = localStorage.getItem('accessToken');
  const user_id = localStorage.getItem('userId');
  const [dates, setDates] = useState([null, null]);
  const [reportrange, setReportrange] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [myLadger, setMyLadger] = useState([]);

  const table = useTable({
    defaultRowsPerPage: 10,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'date',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const searchFormData = (selectedDates) => {
    let startDate = selectedDates[0] ? format(selectedDates[0], 'MM/dd/yyyy') : '';
    let endDate = selectedDates[1] ? format(selectedDates[1], 'MM/dd/yyyy') : '';
    setReportrange(startDate ? startDate + ' - ' + endDate : '');
    loadMyLadger(startDate, endDate);
  };

  const loadMyLadger = async (startDate, endDate) => {
    try {
      const response = await AccountService.fetchMyLadger(startDate, endDate, accessToken, user_id);
      const ledger = Array.isArray(response?.data?.customer_ledger)
        ? response.data.customer_ledger
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setMyLadger(ledger);
      table.onChangePage(null, 0);
    } catch (error) {
      setMyLadger([]);
    }
  };

  const filteredLedger = useMemo(() => {
    if (!searchQuery.trim()) {
      return myLadger;
    }
    const query = searchQuery.toLowerCase();
    return myLadger.filter((item) => {
      const date = (item.date || '').toLowerCase();
      const particulars = (item.particulars || '').toLowerCase();
      const customerName = (item.customer_name || '').toLowerCase();
      const salesPersonName = (item.sales_person_name || '').toLowerCase();
      return (
        date.includes(query) ||
        particulars.includes(query) ||
        customerName.includes(query) ||
        salesPersonName.includes(query)
      );
    });
  }, [myLadger, searchQuery]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    loadMyLadger();

    document.title = 'My Ledger';
    return () => {
      document.title = 'Ebusso';
    };
  }, []);

  useEffect(() => {
    table.onChangePage(null, 0);
  }, [searchQuery]);

  const renderRow = (item, index) => {
    const {
      date,
      particulars,
      customer_name,
      sales_person_name,
      amount,
      payment_type,
      balance,
    } = item;

    const amountNumber = parseFloat(amount);
    const balanceNumber = parseFloat(balance);

    const creditAmount = payment_type === 'c' && Number.isFinite(amountNumber) ? amountNumber.toFixed(2) : '0.00';
    const debitAmount = payment_type === 'd' && Number.isFinite(amountNumber) ? amountNumber.toFixed(2) : '0.00';
    const formattedBalance = Number.isFinite(balanceNumber) ? balanceNumber.toFixed(2) : '0.00';

    return (
      <TableRow
        key={`${date}-${index}-${amount}`}
        className="table-cell-style"
        // sx={{
        //   backgroundColor: payment_type === "c" ? "#b1edb1" : "transparent",
        //   "&:hover": {
        //     backgroundColor: payment_type === "c" ? "#a0dd9f" : "rgba(145, 158, 171, 0.04)",
        //   },
        // }}
      >
        <TableCell>{date || ''}</TableCell>
        <TableCell>{particulars || ''}</TableCell>
        <TableCell>{customer_name || ''}</TableCell>
        <TableCell>{sales_person_name || ''}</TableCell>
        <TableCell align="right" sx={{ color: "primary.main", fontWeight: "500" }}>{creditAmount}</TableCell>
        <TableCell align="right" sx={{ color: "error.main", fontWeight: "500" }}>{debitAmount}</TableCell>
        <TableCell align="right">{formattedBalance}</TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center">
              <Grid2 size={{ xs: 12, md: 5}}>
                <Typography className="page-title">My Ledger</Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  placeholder="Search by date, particulars, customer name, or collected by..."
                  value={searchQuery}
                  size="small"
                  fullWidth
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} height={20} sx={{ color: "#637381" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                            }}
                            edge="end"
                          >
                            <Iconify icon="eva:close-fill" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  onChange={(value) => {
                    setDates(value);
                    if (value && value[0] && value[1]) {
                      searchFormData(value);
                    }
                  }}
                />
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer
       
          >
            <Table
              size="medium"
              className="table-cell-border"
            >
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell width="150px">
                    Date
                  </TableCell>
                  <TableCell width="150px">
                    Particulars
                  </TableCell>
                  <TableCell width="250px">
                    Customer Name
                  </TableCell>
                  <TableCell width="200px">
                    Collected By
                  </TableCell>
                  <TableCell
                    align="right"
                    width="150px"
                  >
                    Credit
                  </TableCell>
                  <TableCell
                    align="right"
                    width="150px"
                  >
                    Debit
                  </TableCell>
                  <TableCell
                    align="right"
                    width="150px"
                  >
                    Balance
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLedger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#637381",
                          fontSize: "0.75rem",
                          lineHeight: 1.5,
                        }}
                      >
                        {myLadger.length === 0
                          ? "No ledger data available."
                          : "No results found matching your search."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLedger
                    .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                    .map((item, index) => renderRow(item, index))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="flex-end" alignItems="center" pt={2}>
            <TablePaginationCustom
              className="pagination-styles"
              page={table.page}
              dense={table.dense}
              count={filteredLedger.length}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
         
            />
          </Box>

        </Paper>
      </Box>
    </>
  );
};

export default MyLadger;
